import { useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { getRangeLength, rangeNormalize } from "../../app-utils/range.js";
import { type Contig, type Range } from "../../graphql.js";
import { type IGenomeBrowserState, type IGenomeViewerStateLayout } from "../../redux/modules/genome-browser.js";
import { type IAppState } from "../../redux/reducer.js";
import { type GenomeViewerData } from "./GenomeViewerTracksList.js";

export const useGenomeViewerData = (): GenomeViewerData => {
  const isLoading = useSelector<IAppState, IGenomeBrowserState["isLoading"]>(
    (state) => state.genomeBrowser.isLoading,
    shallowEqual,
  );
  const layout = useSelector<IAppState, IGenomeViewerStateLayout>((state) => state.genomeBrowser.layout, shallowEqual);
  const range = useSelector<IAppState, Range>((state) => state.genomeBrowser.range, shallowEqual);
  const contig = useSelector<IAppState, Contig>((state) => state.genomeBrowser.contig);
  const normalize = useMemo(
    () => rangeNormalize(layout.trackWidth)(range ?? { start: 0, stop: 0 }),
    [layout.trackWidth, range],
  );
  const baseWidth = useMemo(
    () => (normalize((range?.stop ?? 0) + 1) - normalize(range?.start ?? 0)) / getRangeLength(range),
    [normalize, range],
  );

  return { layout, range, contig, normalize, baseWidth, isLoading };
};
